import React, { useEffect, useRef, useState } from 'react';
import CoachReview from './CoachReview';
import { feelingEmoji, formatDuration, formatKg, formatNumber } from './strengthFormat';

function recordValue(record, lang) {
  switch (record.type) {
    case 'HeaviestWeight':
    case 'BestEstimatedMax':
      return formatKg(record.value, lang);
    case 'LongestHold':
      return formatDuration(record.value);
    default:
      return `${formatNumber(record.value, lang)} reps`;
  }
}

function WorkoutSummary({ api, t, lang, result, stravaStatus, onDone, onGoToStrava }) {
  const { personalRecords = [] } = result;
  // Starts with the instant rules review stored at finish; the AI review replaces it.
  const [session, setSession] = useState(result.session);
  const [reviewing, setReviewing] = useState(true);
  const requested = useRef(false);
  const status = session.stravaSyncStatus;

  useEffect(() => {
    if (requested.current) return;
    requested.current = true;
    api.review(result.session.id)
      .then(reviewed => { if (reviewed) setSession(prev => ({ ...prev, ...reviewed })); })
      .catch(e => console.error('Coach review failed:', e))
      .finally(() => setReviewing(false));
  }, [api, result.session.id]);

  return (
    <div className="st-card st-summary">
      <div className="st-summary-hero">
        <span className="st-summary-icon" aria-hidden="true">{personalRecords.length > 0 ? '🏆' : '💪'}</span>
        <div>
          <h2>{t('strength.summary.title')} {session.feeling ? feelingEmoji(session.feeling) : ''}</h2>
          <p className="st-muted">{session.name}</p>
        </div>
      </div>

      <dl className="st-stats">
        <div><dt>{t('strength.summary.duration')}</dt><dd>{t('strength.summary.minutes', { count: session.durationMinutes ?? 0 })}</dd></div>
        <div><dt>{t('strength.summary.sets')}</dt><dd>{session.workingSets}</dd></div>
        <div><dt>{t('strength.summary.volume')}</dt><dd>{formatKg(Math.round(session.volumeKg), lang)}</dd></div>
      </dl>

      {personalRecords.length > 0 && (
        <section>
          <h3>{t('strength.summary.records')}</h3>
          <ul className="st-records">
            {personalRecords.map(r => (
              <li key={`${r.exerciseKey}-${r.type}`}>
                <strong>{r.exerciseName}</strong> · {t(`strength.records.${r.type}`)}: {recordValue(r, lang)}
                <span className="st-muted"> ({t('strength.summary.was', { value: recordValue({ ...r, value: r.previous }, lang) })})</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <CoachReview t={t} session={session} loading={reviewing} />

      <div className={`st-strava-note st-strava-${status}`}>
        {status === 'Pending' && <>🔄 {t('strength.summary.stravaPending')}</>}
        {status === 'NotConnected' && (
          <>
            {t('strength.summary.stravaNotConnected')}
            {stravaStatus?.configured && (
              <button type="button" className="st-link-btn" onClick={onGoToStrava}>{t('strength.strava.connect')} →</button>
            )}
          </>
        )}
      </div>

      <button type="button" className="st-btn st-btn-primary" onClick={onDone}>{t('strength.summary.done')}</button>
    </div>
  );
}

export default WorkoutSummary;
